import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { BrevoSyncJob } from "@/hooks/useBrevoSync";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Clock, 
  ChevronDown, 
  ChevronUp
} from "lucide-react";

interface BrevoSyncStatusProps {
  activeJob: BrevoSyncJob | null;
  recentJobs: BrevoSyncJob[];
}

const getStatusIcon = (status: BrevoSyncJob["status"]) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    case "failed":
      return <XCircle className="h-3.5 w-3.5 text-destructive" />;
    case "running":
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />;
    case "pending":
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
};

const getStatusBadge = (status: BrevoSyncJob["status"]) => {
  switch (status) {
    case "completed":
      return <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs py-0 h-5">Done</Badge>;
    case "failed":
      return <Badge variant="destructive" className="text-xs py-0 h-5">Failed</Badge>;
    case "running":
      return <Badge variant="default" className="text-xs py-0 h-5">Running</Badge>;
    case "pending":
      return <Badge variant="outline" className="text-xs py-0 h-5">Pending</Badge>;
  }
};

const JobProgress = ({ job }: { job: BrevoSyncJob }) => {
  const processed = job.synced_count + job.failed_count;
  const progress = job.total_users > 0 ? (processed / job.total_users) * 100 : 0;

  return (
    <div className="flex items-center gap-3 flex-1">
      <Progress value={progress} className="h-1.5 flex-1" />
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {processed}/{job.total_users} ({Math.round(progress)}%)
      </span>
    </div>
  );
};

const JobItem = ({ job }: { job: BrevoSyncJob }) => {
  const [showErrors, setShowErrors] = useState(false);
  const startedAt = job.started_at ? new Date(job.started_at) : null;
  const completedAt = job.completed_at ? new Date(job.completed_at) : null;
  const isActive = job.status === "running" || job.status === "pending";

  return (
    <div className="border rounded-md px-2 py-1.5 space-y-1">
      <div className="flex items-center gap-2 min-h-[24px]">
        {getStatusIcon(job.status)}
        <span className="text-xs font-medium capitalize">
          {job.job_type.replace(/_/g, " ")}
        </span>
        {getStatusBadge(job.status)}
        
        {isActive && <JobProgress job={job} />}
        
        {!isActive && (
          <div className="flex items-center gap-2 ml-auto text-xs text-muted-foreground">
            <span className="text-green-600">{job.synced_count} synced</span>
            {job.failed_count > 0 && (
              <span className="text-destructive">{job.failed_count} failed</span>
            )}
            {completedAt && startedAt && (
              <span>• {Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)}s</span>
            )}
            <span>• {startedAt ? formatDistanceToNow(startedAt, { addSuffix: true }) : "Not started"}</span>
          </div>
        )}
      </div>

      {job.errors && job.errors.length > 0 && (
        <Collapsible open={showErrors} onOpenChange={setShowErrors}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 text-xs text-destructive px-1">
              {showErrors ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              {job.errors.length} error{job.errors.length !== 1 ? "s" : ""}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-1 p-2 bg-destructive/10 rounded text-xs max-h-24 overflow-y-auto">
              {job.errors.map((error, i) => (
                <div key={i} className="text-destructive truncate">{error}</div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};

export const BrevoSyncStatus = ({ activeJob, recentJobs }: BrevoSyncStatusProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasActiveJob = activeJob && (activeJob.status === "running" || activeJob.status === "pending");
  const completedJobs = recentJobs.filter(j => 
    j.status === "completed" || j.status === "failed"
  ).slice(0, 5);

  // If no jobs at all, don't show anything
  if (!activeJob && completedJobs.length === 0) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
          <span className="text-sm font-medium">Sync Status</span>
          <div className="flex items-center gap-1">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent>
          <div className="p-2 space-y-1.5">
            {hasActiveJob && (
              <p className="text-xs text-muted-foreground px-1">
                Sync continues in background - safe to close page
              </p>
            )}
            
            {/* Active Job */}
            {activeJob && <JobItem job={activeJob} />}

            {/* Recent Jobs (if not showing active) */}
            {completedJobs.length > 0 && !hasActiveJob && (
              <>
                <div className="text-xs font-medium text-muted-foreground px-1">Recent syncs</div>
                {completedJobs.slice(0, 3).map((job) => (
                  <JobItem key={job.id} job={job} />
                ))}
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
